import type { DynamicQuestionAnswerValue } from 'src/components/molecules';
import type { DisclosureCaseByIdQuery } from 'src/graphql/generated';

export type AnswersState = Record<string, DynamicQuestionAnswerValue>;

export function removeQaAttachmentFromAnswerValue(
  value: DynamicQuestionAnswerValue,
  attachmentId: string,
): DynamicQuestionAnswerValue {
  const hasQa = value.qaAnswerAttachments?.some((a) => a?.id === attachmentId);
  const hasEvidence = value.evidenceUploadFiles?.some(
    (f) => f.options?.metadata?.qaAttachmentId === attachmentId,
  );
  if (!hasQa && !hasEvidence) {
    return value;
  }
  const next: DynamicQuestionAnswerValue = { ...value };
  if (hasQa && next.qaAnswerAttachments?.length) {
    const qa = next.qaAnswerAttachments.filter((a) => a?.id !== attachmentId);
    if (qa.length > 0) {
      next.qaAnswerAttachments = qa;
    } else {
      delete next.qaAnswerAttachments;
    }
  }
  if (hasEvidence && next.evidenceUploadFiles?.length) {
    const files = next.evidenceUploadFiles.filter(
      (f) => f.options?.metadata?.qaAttachmentId !== attachmentId,
    );
    if (files.length > 0) {
      next.evidenceUploadFiles = files;
      next.fileNames = files.map((f) => f.options.file.name);
    } else {
      delete next.evidenceUploadFiles;
      delete next.fileNames;
    }
  }
  return next;
}

export function patchAnswersRemovingAttachment(
  prev: AnswersState,
  attachmentId: string,
): AnswersState {
  let changed = false;
  const next: AnswersState = { ...prev };
  for (const key of Object.keys(prev)) {
    const v = prev[key];
    if (v == null || typeof v !== 'object') continue;
    const patched = removeQaAttachmentFromAnswerValue(v, attachmentId);
    if (patched !== v) {
      next[key] = patched;
      changed = true;
    }
  }
  return changed ? next : prev;
}

export function patchDisclosureCaseRemovingAttachment(
  data: DisclosureCaseByIdQuery,
  questionId: string,
  attachmentId: string,
): DisclosureCaseByIdQuery {
  const disclosure = data.disclosureCaseById;
  if (!disclosure?.answers?.length) {
    return data;
  }
  const nextAnswers = disclosure.answers.map((ans) => {
    if (String(ans?.questionId ?? '') !== questionId) {
      return ans;
    }
    const raw = ans?.value;
    if (raw == null || typeof raw !== 'object') {
      return ans;
    }
    const v = raw as DynamicQuestionAnswerValue;
    const patched = removeQaAttachmentFromAnswerValue(v, attachmentId);
    if (patched === v) {
      return ans;
    }
    return { ...ans, value: patched };
  });
  return {
    ...data,
    disclosureCaseById: {
      ...disclosure,
      answers: nextAnswers,
    },
  };
}

export function cloneAnswersState(prev: AnswersState): AnswersState {
  return JSON.parse(JSON.stringify(prev)) as AnswersState;
}
